'use client';

import { memo, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { QuoteActions } from './QuoteActions';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { Button, ErrorBoundary, Icon } from '@benzinga/core-ui';
import { isMobile } from '@benzinga/device-utils';
import { DEFAULT_STICKY_NAV_OFFSET_PX, useHorizontalScrollIntoView, useRecentTickers, useStickyNavOffset } from '@benzinga/hooks';
import { AddToWatchlist } from '@benzinga/watchlist-ui';
import { GetReportButton } from '@benzinga/ticker-ui';
import { CompanyLogo } from '../CompanyLogo';
import { Wiim } from '../Wiim';
import { StockInfoMessages } from './StockInfoMesages';
import type { ActiveTab, BaseQuoteProfile, TabProp } from '../../utils/quote/types';
import { generateStockInfoMessages } from '../../utils/quote/stock-info-message-generator';
import { isRealtimeQuoteStale } from '../../utils/quote/realtime-quote';
import { QuoteHeaderPriceBox } from './QuoteHeaderPriceBox';
import SymbolExchangeInformation from './SymbolExchangeInformation';
import type { Quote } from '@benzinga/quotes-v3-manager';
import { useQuoteSubscription } from '@benzinga/quotes-v3-manager-hooks';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { deriveQuoteActiveTabFromPathname } from '../../utils/quote/active-tab';
import { quoteExchangeLabel } from '../../utils/quote/private-rich-quote';
import styles from './QuoteHeader.module.scss';
export interface QuoteHeaderProps {
  activeTab: ActiveTab;
  profile: BaseQuoteProfile;
  symbol: string;
  logo?: ReactNode;
  banners?: ReactNode;
  tabs?: ReactNode;
}
const headerTagKeyForSEO = ['overview', 'news', 'earnings', 'events', 'holdings', 'option-chain', 'guidance', 'dividends', 'analyst-ratings', 'insider-trades', 'short-interest', 'unusual-options'];
export const QuoteHeader = memo(({
  activeTab,
  profile,
  symbol,
  logo,
  banners,
  tabs: tabsSlot
}: QuoteHeaderProps) => {
  'use memo';

  const {
    t,
    i18n
  } = useTranslation('quote');
  const [areQuoteActionsHidden, setAreQuoteActionsHidden] = useState(false);
  const [areTabsSticky, setAreTabsSticky] = useState(false);
  const tabsSentinelRef = useRef<HTMLDivElement>(null);
  const isGlobalSite = i18n.language !== 'en';
  const templateVariant = isGlobalSite ? 'simple' : 'default';
  const stickyNavOffset = useStickyNavOffset(DEFAULT_STICKY_NAV_OFFSET_PX);
  const fields = useMemo<Set<keyof Quote>>(() => new Set(['symbol', 'price', 'changePercent', 'change', 'lastTradeTime', 'time', 'previousClose', 'close']), []);
  const quotes = useQuoteSubscription(symbol, fields);
  const stockInfoMessages = useMemo(() => {
    return generateStockInfoMessages(symbol, profile?.richQuoteData?.companyStandardName, profile?.splitsSummary || null, profile?.dividendSummary || null, profile?.earningsSummary || null, profile?.conferenceCallsSummary || null, profile?.maSummary || null, t, i18n.language);
  }, [symbol, profile?.richQuoteData?.companyStandardName, profile?.splitsSummary, profile?.dividendSummary, profile?.earningsSummary, profile?.conferenceCallsSummary, profile?.maSummary, t, i18n.language]);
  const [, addRecentTicker] = useRecentTickers();
  useEffect(() => {
    if (symbol && !profile.isPrivate) {
      addRecentTicker(symbol);
    }
  }, [symbol, addRecentTicker, profile.isPrivate]);
  const scrollToElement = useHorizontalScrollIntoView('active-tab-item');
  useEffect(() => {
    isMobile() && scrollToElement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const sentinel = tabsSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      setAreTabsSticky(!entry.isIntersecting);
    }, {
      rootMargin: `-${Math.max(stickyNavOffset, 1)}px 0px 0px 0px`,
      threshold: 0
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyNavOffset]);
  const name = (profile?.isPrivate ? profile?.privateCompanyName : profile?.isCrypto ? profile?.cryptoData?.name || profile?.richQuoteData?.description : profile?.richQuoteData?.companyStandardName) || '';
  const tabs: TabProp[] = Array.isArray(profile?.tabs) ? profile.tabs : [];
  const session = useContext(SessionContext);
  const handleShowMoreQuoteActions = () => {
    setAreQuoteActionsHidden(!areQuoteActionsHidden);
    session.getManager(TrackingManager).trackPageEvent('click', {
      page_section: 'quote_actions_more',
      page_tab: activeTab
    });
  };
  const isStaleRealtimeQuote = isRealtimeQuoteStale(quotes, profile?.richQuoteData);
  const headerQuotes = isStaleRealtimeQuote ? undefined : quotes;
  const liveQuoteTime = headerQuotes?.lastTradeTime ?? headerQuotes?.time;
  const isRealTime = Boolean(liveQuoteTime && (headerQuotes?.price || headerQuotes?.askPrice));
  const headerTag = activeTab && headerTagKeyForSEO.includes(activeTab) ? t(`Quote.HeaderTags.${activeTab}`, {
    stockName: name,
    symbol
  }) : null;
  return <>
      <div className={classNames(styles.quoteHeaderWrapper, 'quote-header', {
      [styles.simple]: templateVariant === 'simple',
      [styles.isCrypto]: profile.isCrypto,
      [styles.noTabs]: tabs.length === 0
    })}>
        <div className="header-top-half flex items-center justify-between w-full main-div">
          <div className="flex min-w-0 flex-col md:max-w-[calc(100%-250px)]">
            <div className="lg:text-lg flex items-center">
              <div className="h-16 w-16 shrink-0">
                {logo ?? <CompanyLogo logo={profile?.logoUrl ?? ''} name={name} symbol={symbol} wrapperClassName="ticker-logo" />}
              </div>
              <div className="flex gap-2 header-left-text items-center">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-start ml-2">
                    <h2 className="quote-name text-bzblue-1000 text-2xl md:text-3xl">{headerTag || name}</h2>
                    <div className="flex items-center gap-3 mt-1">
                      <SymbolExchangeInformation className="sm:text-sm text-grey-darker" exchange={quoteExchangeLabel(profile?.richQuoteData?.bzExchange, profile.isPrivate)} otcTier={(profile?.richQuoteData?.otcTier ?? null) as string} symbol={symbol} />
                      <div className="hidden md:flex gap-3 mr-2">
                        {!profile.isPrivate && <div className="md:ml-1 items-center w-full add-to-watchlist-wrapper">
                            <AddToWatchlist buttonText={t('Buttons.watchlist')} buttonVariant={templateVariant === 'simple' ? 'dark-blue' : 'flat-blue'} showIcon={true} symbol={profile?.symbol} variant="default" />
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!profile.isPrivate && <span className="md:hidden">
              <AddToWatchlist buttonVariant="flat-blue" symbol={profile?.symbol} variant="compact" />
            </span>}
          {!profile.isPrivate && <div className="quote-actions-desktop">
              <QuoteActionsContainer templateVariant={templateVariant} isCrypto={profile.isCrypto} symbol={symbol} />
            </div>}
        </div>
        {!profile.isPrivate && <div className="quote-actions-tablet main-div">
            <QuoteActionsContainer templateVariant={templateVariant} isCrypto={profile.isCrypto} symbol={symbol} />
          </div>}
        <div className="main-div flex justify-between">
          <QuoteHeaderPriceBox isCrypto={profile.isCrypto} isRealTime={isRealTime} profile={profile} quotes={headerQuotes as Quote} />
        </div>

        {!profile.isPrivate && <div className="main-div md:hidden w-full flex flex-col gap-2 mb-4">
            <div className="flex gap-2 items-center w-full">
              <GetReportButton ticker={profile?.symbol} variant={'flat-blue'} />
              <Button aria-label="Toggle more quote actions" className={classNames('more-quote-actions-button', {
            active: areQuoteActionsHidden
          })} onClick={handleShowMoreQuoteActions} variant="flat-light-blue">
                {<Icon icon={areQuoteActionsHidden ? faChevronUp : faChevronDown} />}
              </Button>
            </div>
            {areQuoteActionsHidden && <QuoteActionsContainer showGetReportButton={false} templateVariant={templateVariant} isCrypto={profile.isCrypto} symbol={symbol} />}
          </div>}
      </div>
      {(tabsSlot || tabs.length > 0) && <>
          <div ref={tabsSentinelRef} aria-hidden className={styles.tabsSentinel} />
          <div className={classNames(styles.quoteTabsSticky, 'main-quote-tabs-wrapper', {
        [styles.simple]: templateVariant === 'simple',
        [styles.isSticky]: areTabsSticky
      })}>
            <div className="main-quote-tabs">
              {tabsSlot ?? <QuoteTabLinks activeTab={activeTab} name={name} symbol={symbol} tabs={tabs} />}
            </div>
          </div>
        </>}
      <div className="main-div flex min-h-0 flex-col gap-1.5 empty:hidden [&:not(:empty)]:!pt-4">
        {banners ?? <>
            {profile?.wiim && <ErrorBoundary name="wiim">
                <Wiim wiim={profile?.wiim ?? null} />
              </ErrorBoundary>}
            <ErrorBoundary name="stock-info-messages">
              <StockInfoMessages messages={stockInfoMessages} />
            </ErrorBoundary>
          </>}
      </div>
    </>;
});
export const QuoteTabLinks = ({
  activeTab: activeTabProp,
  name,
  symbol,
  tabs
}: {
  activeTab?: ActiveTab;
  name: string;
  symbol: string;
  tabs: TabProp[];
}) => {
  const {
    t,
    i18n
  } = useTranslation('quote');
  const session = useContext(SessionContext);
  const pathname = usePathname();
  const activeTab = activeTabProp ?? deriveQuoteActiveTabFromPathname(pathname);
  const isGlobalSite = i18n.language !== 'en';
  return <>
      {tabs.map((tab, index) => {
      if (activeTab === tab.key) {
        return <span className="tab active" id="active-tab-item" key={index} title={`${name} (${symbol}) ${t(`Quote.Nav.${tab.key}`)}`}>
              {t(`Quote.Nav.${tab.key}`)}
            </span>;
      }
      const tabHref = isGlobalSite && tab.key === 'government-trades' ? `https://www.benzinga.com/government-trades/securities/${symbol}` : tab.href;
      const isExternalTab = /^https?:\/\//.test(tabHref);
      const trackTabClick = () => session.getManager(TrackingManager).trackPageEvent('click', {
        page_section: 'quote_tab_nav',
        page_tab: tab.key
      });
      const tabTitle = `${name} (${symbol}) ${t(`Quote.Nav.${tab.key}`)}`;
      const tabLabel = t(`Quote.Nav.${tab.key}`);
      return isExternalTab ? <a className="tab" href={tabHref} key={index} onClick={trackTabClick} title={tabTitle}>
            {tabLabel}
          </a> : <Link className="tab" href={tabHref} key={index} onClick={trackTabClick} prefetch={false} title={tabTitle}>
            {tabLabel}
          </Link>;
    })}
    </>;
};
export const QuoteHeaderBanners = ({
  profile,
  symbol
}: {
  profile: BaseQuoteProfile;
  symbol: string;
}) => {
  const {
    t,
    i18n
  } = useTranslation('quote');
  const stockInfoMessages = generateStockInfoMessages(symbol, profile?.richQuoteData?.companyStandardName, profile?.splitsSummary || null, profile?.dividendSummary || null, profile?.earningsSummary || null, profile?.conferenceCallsSummary || null, profile?.maSummary || null, t, i18n.language);
  return <>
      {profile?.wiim && <ErrorBoundary name="wiim">
          <Wiim wiim={profile?.wiim ?? null} />
        </ErrorBoundary>}
      <ErrorBoundary name="stock-info-messages">
        <StockInfoMessages messages={stockInfoMessages} />
      </ErrorBoundary>
    </>;
};
const QuoteActionsContainer = ({
  showGetReportButton = true,
  templateVariant,
  isCrypto,
  symbol
}) => <QuoteActions className="bottom-buttons" compareBrokersButtonVariant={templateVariant === 'simple' ? 'light-gray' : 'flat-light-blue'} getReportButtonVariant={templateVariant === 'simple' ? 'dark-blue' : 'flat-blue'} isCrypto={isCrypto} quoteBuyButtonVariant={templateVariant === 'simple' ? 'light-gray' : 'flat-light-blue'} showGetReportButton={showGetReportButton} symbol={symbol} showCompareBrokersButton={false} />;